import React, { useState, useEffect, useRef } from 'react';
import { AppDrawer } from './ui/AppDrawer';
import { Button } from './ui/button';
import { IntegerQuantityInput } from './IntegerQuantityInput';
import { formatQty } from '../lib/qtyUtils';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Loader2, Package, CheckCircle2, AlertCircle, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { purchaseOrdersAPI, pausePolling, resumePolling } from '../lib/api';
import { PODocumentsPanel } from './po/PODocumentsPanel';

/**
 * PartialReceiveModal - Drawer for receiving PO line items
 * 
 * Features:
 * - Shows all line items with current receiving status
 * - Input field for "Receive Now" quantity per item
 * - Validates against remaining quantity
 * - Auto-calculates status (Partially Received vs Received)
 * - Shows visual feedback for validation errors
 * 
 * P0 FIX: Added AbortController and submission guard to prevent
 * double-submissions and properly handle request cancellation.
 * 
 * P0 FIX: Pauses all background polling when drawer is open to prevent
 * connection saturation (browser 6-connection limit).
 */
export const PartialReceiveModal = ({ 
  open, 
  onOpenChange, 
  purchaseOrder, 
  onSuccess 
}) => {
  const [receiveQuantities, setReceiveQuantities] = useState({});
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [failureDiag, setFailureDiag] = useState(null);
  
  // P0 FIX: Track if a submission is in-flight to prevent double-click
  const submittingRef = useRef(false);
  const abortControllerRef = useRef(null);

  // P0 FIX: Pause/resume polling when drawer opens/closes
  useEffect(() => {
    if (open) {
      pausePolling('PartialReceiveModal-open');
    } else {
      resumePolling();
    }
    return () => { if (open) resumePolling(); };
  }, [open]);

  // Initialize receive quantities when drawer opens
  useEffect(() => {
    if (open && purchaseOrder) {
      const initialQuantities = {};
      purchaseOrder.line_items.forEach(li => { initialQuantities[li.id] = 0; });
      setReceiveQuantities(initialQuantities);
      setErrors({});
      setFailureDiag(null);
      submittingRef.current = false;
    }
  }, [open, purchaseOrder]);
  
  // P0 FIX: Handle explicit close by user
  const handleOpenChange = (newOpen) => {
    if (!newOpen && saving) {
      console.log('[PartialReceiveModal] Close attempted while saving - ignoring');
      return;
    }
    if (!newOpen && abortControllerRef.current) {
      console.log('[PartialReceiveModal] Aborting request due to user close');
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    onOpenChange(newOpen);
  };

  const getRemainingQty = (lineItem) => lineItem.quantity - (lineItem.quantity_received || 0);

  const validateQuantity = (lineItemId, value, lineItem) => {
    const remaining = getRemainingQty(lineItem);
    const numValue = parseInt(value) || 0;
    if (numValue < 0) return 'Cannot be negative';
    if (numValue > remaining) return `Cannot exceed remaining (${formatQty(remaining)})`;
    return null;
  };

  const handleQuantityChange = (lineItemId, value, lineItem) => {
    const numValue = value === '' ? 0 : parseInt(value) || 0;
    setReceiveQuantities(prev => ({ ...prev, [lineItemId]: numValue }));
    const error = validateQuantity(lineItemId, numValue, lineItem);
    setErrors(prev => ({ ...prev, [lineItemId]: error }));
  };

  const handleReceiveAllForItem = (lineItem) => {
    const remaining = getRemainingQty(lineItem);
    handleQuantityChange(lineItem.id, remaining, lineItem);
  };

  const handleReceiveAllItems = () => {
    const newQuantities = {};
    const newErrors = {};
    purchaseOrder.line_items.forEach(li => {
      const remaining = getRemainingQty(li);
      newQuantities[li.id] = remaining;
      newErrors[li.id] = null;
    });
    setReceiveQuantities(newQuantities);
    setErrors(newErrors);
  };

  const hasErrors = Object.values(errors).some(e => e !== null);
  const hasQuantitiesToReceive = Object.values(receiveQuantities).some(q => q > 0);

  const calculateNewStatus = () => {
    if (!purchaseOrder) return null;
    let totalOrdered = 0;
    let totalWillBeReceived = 0;
    purchaseOrder.line_items.forEach(li => {
      totalOrdered += li.quantity;
      const currentReceived = li.quantity_received || 0;
      const toReceive = receiveQuantities[li.id] || 0;
      totalWillBeReceived += currentReceived + toReceive;
    });
    if (totalWillBeReceived >= totalOrdered) return 'Received';
    else if (totalWillBeReceived > 0) return 'Partially Received';
    return purchaseOrder.status;
  };

  const handleSubmit = async () => {
    // P0 FIX: Guard against double-submission
    if (hasErrors || !hasQuantitiesToReceive || saving || submittingRef.current) return;

    submittingRef.current = true;
    setSaving(true);
    setFailureDiag(null);
    
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const clientRequestId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2,10)}`;
    const startTime = Date.now();
    
    try {
      // P0 FIX: Warm up the connection path
      try {
        const warmupUrl = `${process.env.REACT_APP_BACKEND_URL}/api/purchase-orders/${purchaseOrder.id}`;
        await fetch(warmupUrl, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}`, 'Content-Type': 'application/json' },
          signal: AbortSignal.timeout(5000)
        });
      } catch (warmupErr) {
        console.warn('[PartialReceiveModal] Connection warm-up failed:', warmupErr.message);
      }
      
      await new Promise(resolve => setTimeout(resolve, 50));
      
      const items = purchaseOrder.line_items
        .filter(li => (receiveQuantities[li.id] || 0) > 0)
        .map(li => ({
          line_item_id: li.id,
          quantity_received: Number((parseInt(li.quantity_received) || 0) + (parseInt(receiveQuantities[li.id]) || 0))
        }));

      if (items.length === 0) {
        toast.info('No items to receive. Enter quantities greater than 0.');
        setSaving(false);
        submittingRef.current = false;
        return;
      }
      
      const response = await purchaseOrdersAPI.receiveItems(purchaseOrder.id, items, {
        signal: abortController.signal,
        headers: { 'X-Client-Request-Id': clientRequestId }
      });
      const { new_status, fully_received_notification, status_changed } = response.data;

      if (new_status === 'Received' && fully_received_notification?.attempted) {
        if (fully_received_notification.sent) toast.success('PO marked as Fully Received. Notification email sent.');
        else if (fully_received_notification.reason === 'missing_job_number') toast.info('PO marked as Fully Received. No notification sent (no job number).');
        else if (fully_received_notification.reason === 'missing_to_address') toast.info('PO marked as Fully Received. No notification sent (no recipient email configured).');
        else if (fully_received_notification.reason === 'email_send_failed') toast.warning('PO marked as Fully Received, but the notification email failed to send.');
      } else if (status_changed) {
        toast.success(`Items received. PO status updated to "${new_status}"`);
      } else {
        toast.success('Items received successfully');
      }

      onSuccess?.(response.data.purchase_order);
      onOpenChange(false);
    } catch (error) {
      if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return;
      
      const respHeaders = error.response?.headers || {};
      const elapsed = Date.now() - startTime;
      const diag = {
        requestId: clientRequestId,
        serverHandled: respHeaders['x-server-handled'] === '1',
        serverTimeMs: respHeaders['x-server-time-ms'] ? `${respHeaders['x-server-time-ms']} ms` : 'N/A',
        lastStep: respHeaders['x-receive-step'] || 'N/A',
        httpStatus: error.response?.status || 'No response',
        clientElapsed: `${elapsed} ms`,
        cfRay: respHeaders['x-cf-ray-echo'] || 'N/A'
      };
      setFailureDiag(diag);

      let errorMessage = 'Failed to receive items';
      if (error.response?.data?.detail) errorMessage = error.response.data.detail;
      else if (error.response?.data?.message) errorMessage = error.response.data.message;
      else if (error.message && error.message !== 'Network Error') errorMessage = error.message;
      
      toast.error(`${errorMessage} (Request ID: ${clientRequestId.slice(0, 8)})`);
    } finally {
      setSaving(false);
      submittingRef.current = false;
      abortControllerRef.current = null;
    }
  };

  if (!purchaseOrder) return null;

  const predictedStatus = calculateNewStatus();
  const totalToReceive = Object.values(receiveQuantities).reduce((sum, q) => sum + q, 0);

  return (
    <AppDrawer
      open={open}
      onOpenChange={handleOpenChange}
      title={
        <span className="flex items-center gap-2">
          <Package className="w-5 h-5" />
          Receive Items - PO #{purchaseOrder.po_number}
        </span>
      }
      description="Enter the quantity received for each item. The PO status will update automatically based on receiving progress."
      size="lg"
      isDirty={hasQuantitiesToReceive}
      data-testid="partial-receive-drawer"
      footer={
        <>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={saving} data-testid="receive-cancel-btn">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving || hasErrors || !hasQuantitiesToReceive} data-testid="receive-items-submit-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {hasQuantitiesToReceive ? `Receive ${totalToReceive} Item${totalToReceive !== 1 ? 's' : ''}` : 'Enter quantities to receive'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Summary Bar */}
        <div className="bg-muted/50 rounded-lg p-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm">
              <span className="text-muted-foreground">Current Status: </span>
              <Badge variant="outline">{purchaseOrder.status}</Badge>
            </div>
            <div className="text-sm">
              <span className="text-muted-foreground">Progress: </span>
              <span className="font-medium">{formatQty(purchaseOrder.total_quantity_received)} / {formatQty(purchaseOrder.total_quantity_ordered)}</span>
            </div>
          </div>
          {hasQuantitiesToReceive && (
            <div className="mt-2 pt-2 border-t border-border">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">After receiving {totalToReceive} item(s):</span>
                <Badge variant={predictedStatus === 'Received' ? 'default' : 'secondary'}
                  className={predictedStatus === 'Received' ? 'bg-green-600' : 'bg-blue-600'}>
                  {predictedStatus}
                </Badge>
              </div>
            </div>
          )}
        </div>

        {/* Quick Action */}
        <div className="flex justify-end">
          <Button type="button" variant="outline" size="sm" onClick={handleReceiveAllItems}>
            Receive All Remaining
          </Button>
        </div>

        {/* Line Items */}
        <div className="border rounded-lg divide-y" data-testid="receive-items-list">
          {purchaseOrder.line_items.map((li) => {
            const remaining = getRemainingQty(li);
            const currentReceived = li.quantity_received || 0;
            const toReceive = receiveQuantities[li.id] || 0;
            const isFullyReceivedAfter = (currentReceived + toReceive) >= li.quantity;
            const error = errors[li.id];

            return (
              <div key={li.id} className={`p-3 sm:p-4 ${isFullyReceivedAfter ? 'bg-green-50' : ''}`} data-testid={`receive-line-item-${li.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate text-sm">{li.item_name}</p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1 flex-wrap">
                      <span>Ordered: <strong>{formatQty(li.quantity)}</strong></span>
                      <span>Received: <strong>{formatQty(currentReceived)}</strong></span>
                      <span className={remaining === 0 ? 'text-green-600' : 'text-amber-600'}>
                        Remaining: <strong>{formatQty(remaining)}</strong>
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {remaining > 0 ? (
                      <div className="flex flex-col">
                        <Label className="text-xs mb-1">Receive Now</Label>
                        <div className="flex items-center gap-2">
                          <IntegerQuantityInput min={0} max={remaining} value={toReceive || ''}
                            onChange={(val) => handleQuantityChange(li.id, val, li)}
                            className={`w-20 text-center ${error ? 'border-red-500' : ''}`}
                            placeholder="0" data-testid={`receive-qty-input-${li.id}`} />
                          <Button type="button" variant="ghost" size="sm" onClick={() => handleReceiveAllForItem(li)} className="text-xs shrink-0">
                            All ({formatQty(remaining)})
                          </Button>
                        </div>
                        {error && (
                          <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />{error}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-green-600">
                        <CheckCircle2 className="w-5 h-5" />
                        <span className="text-sm font-medium">Fully Received</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {purchaseOrder?.id && <PODocumentsPanel poId={purchaseOrder.id} source="receive" title="Packing slip & documents" />}

        {/* Failure diagnostic panel */}
        {failureDiag && (
          <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-xs space-y-1" data-testid="receive-failure-diag">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-red-700 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" /> Request Failed — Diagnostics
              </span>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs"
                onClick={() => {
                  const text = Object.entries(failureDiag).map(([k,v]) => `${k}: ${v}`).join('\n');
                  navigator.clipboard.writeText(text);
                  toast.info('Diagnostics copied');
                }} data-testid="copy-diag-btn">
                <Copy className="w-3 h-3 mr-1" /> Copy
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-muted-foreground">
              <span>Request ID:</span><span className="font-mono text-foreground">{failureDiag.requestId.slice(0, 8)}</span>
              <span>Server handled:</span><span className={failureDiag.serverHandled ? 'text-green-600' : 'text-red-600 font-semibold'}>{failureDiag.serverHandled ? 'Yes' : 'No'}</span>
              <span>Server time:</span><span className="font-mono">{failureDiag.serverTimeMs}</span>
              <span>Last step:</span><span className="font-mono">{failureDiag.lastStep}</span>
              <span>HTTP status:</span><span className="font-mono">{failureDiag.httpStatus}</span>
              <span>Client elapsed:</span><span className="font-mono">{failureDiag.clientElapsed}</span>
            </div>
          </div>
        )}
      </div>
    </AppDrawer>
  );
};

export default PartialReceiveModal;
